export interface PwaInstallEnvironment extends PwaPlatform {
  isSecureContext: boolean;
  isStandalone: boolean;
  hasDeferredPrompt: boolean;
}

export interface PwaPushNotificationEnvironment {
  isSecureContext: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  isStandalone: boolean;
  isIos: boolean;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

export interface PwaBrowserIdentity {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

export interface PwaPlatform {
  isIos: boolean;
  isAndroid: boolean;
  isSafari: boolean;
}

export type PwaPushNotificationSupport = 'ready' | 'requires-ios-standalone' | 'unsupported';

export const detectPwaPlatform = (identity: PwaBrowserIdentity): PwaPlatform => {
  const hasIosUserAgent = /iPad|iPhone|iPod/i.test(identity.userAgent);
  const isTouchMac = identity.platform === 'MacIntel' && identity.maxTouchPoints > 1;
  const isIos = hasIosUserAgent || isTouchMac;

  return {
    isIos,
    isAndroid: /Android/i.test(identity.userAgent),
    isSafari: isIos && /Safari/i.test(identity.userAgent) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(identity.userAgent),
  };
};

export const detectPwaInstallEnvironment = (hasDeferredPrompt: boolean): PwaInstallEnvironment => {
  const navigatorValue = navigator as NavigatorWithStandalone;
  const platform = detectPwaPlatform(navigatorValue);

  return {
    isSecureContext: window.isSecureContext,
    isStandalone: window.matchMedia('(display-mode: standalone)').matches || navigatorValue.standalone === true,
    ...platform,
    hasDeferredPrompt,
  };
};

export const getPwaPushNotificationSupport = (
  environment: PwaPushNotificationEnvironment,
): PwaPushNotificationSupport => {
  if (!environment.isSecureContext) {
    return 'unsupported';
  }

  if (environment.isIos && !environment.isStandalone) {
    return 'requires-ios-standalone';
  }

  if (!environment.hasServiceWorker || !environment.hasPushManager || !environment.hasNotification) {
    return 'unsupported';
  }

  return 'ready';
};

export const detectPwaPushNotificationSupport = (): PwaPushNotificationSupport => {
  const installEnvironment = detectPwaInstallEnvironment(false);

  return getPwaPushNotificationSupport({
    isSecureContext: installEnvironment.isSecureContext,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification: 'Notification' in window,
    isStandalone: installEnvironment.isStandalone,
    isIos: installEnvironment.isIos,
  });
};
